import {
  useQuery,
  UseQueryResult,
  keepPreviousData,
} from '@tanstack/react-query'
import { AxiosInstance } from 'axios'
import {
  ChecklistCategoryAPIResponse,
  ChecklistDocumentSummary,
  ChecklistDocumentFilter,
  ChecklistsResponseBody,
  ChecklistDocument,
} from '@black-bear-energy/black-bear-energy-common'
import { checklistChildIsChecklist } from '../../components/helpers/DocumentExchange/checklistChildIsDocument'
import { useApiWithIDTokenGranted } from '../../state/modules/common'

/**
 * Fetch checklists for a site RFP for populating the Document Exchange site RFP dashboard.
 * @param siteRFPId - The ID of the site RFP.
 * @param search - The search term to filter by.
 * This can be a checklist name, document type, or a reviewer name.
 * @param documentFilter - Describes which documents should be shown in the list.
 * @param category - The checklist category to filter by.
 * @returns A UseQueryResult object containing the site RFP checklists.
 */
export const useSiteRFPChecklists = (
  siteRFPId: number,
  search: string = '',
  documentFilter: ChecklistDocumentFilter = ChecklistDocumentFilter.All,
  category: ChecklistCategoryAPIResponse
): UseQueryResult<ChecklistsResult, Error> => {
  const api = useApiWithIDTokenGranted()

  return useQuery({
    queryKey: [
      'siteRFPChecklists',
      api,
      siteRFPId,
      search,
      documentFilter,
      category,
    ],
    queryFn: () =>
      fetchSiteRFPChecklists(
        api as AxiosInstance,
        siteRFPId,
        search,
        documentFilter,
        category
      ),
    select: transformSiteRFPChecklists,
    placeholderData: keepPreviousData,
    enabled: !!api,
  })
}

/** The result from the useSiteRFPChecklists hook. */
export type ChecklistsResult = {
  records: ChecklistDocumentSummary[]
}

async function fetchSiteRFPChecklists(
  api: AxiosInstance,
  siteRFPId: number,
  search: string,
  documentFilter: ChecklistDocumentFilter,
  category: ChecklistCategoryAPIResponse
): Promise<ChecklistsResponseBody> {
  const response = await api.get<ChecklistsResponseBody>(
    `/site-rfps/${siteRFPId}/checklists`,
    {
      params: {
        search: encodeURIComponent(search),
        filter: documentFilter,
        checklistCategory: category,
      },
    }
  )
  return response.data
}

const transformSiteRFPChecklists = (
  data: ChecklistsResponseBody
): ChecklistsResult => {
  const records = data.map((responseElement) => ({
    ...responseElement,
    lastUpdated: new Date(responseElement.lastUpdated),
    children: responseElement.children.map(convertChild),
  }))
  return {
    records,
  }
}

const convertChild = (child: {
  lastUpdated: string
  review?: { reviewedAt: string } | null
  children?: { lastUpdated: string }[]
}): ChecklistDocumentSummary | ChecklistDocument => {
  if (checklistChildIsChecklist(child)) {
    return {
      ...child,
      lastUpdated: new Date(child.lastUpdated),
      children: child.children.map(convertChild),
    }
  }
  return {
    ...child,
    lastUpdated: new Date(child.lastUpdated),
    review: child.review
      ? { ...child.review, reviewedAt: new Date(child.review.reviewedAt) }
      : null,
  } as ChecklistDocument
}
